import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

// Template152: Luxemburgo (Barrio Latino, Panteón, Luxemburgo, Torre Montparnasse). Todo desde backend.
// page.texts[0]=header, [1]=intro | page.sections[0..3] (imageId, title, body, sabiasQue[0]) | page.llegar | page.images, page.canvasImages
const Template152 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found) return found.src ?? found.url;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const headerText = page?.texts?.[0]?.content;
	const logoImage = getCanvasImageUrl("logo");
	const introText = page?.texts?.[1]?.content;
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const section0 = sections[0];
	const section1 = sections[1];
	const section2 = sections[2];
	const section3 = sections[3];
	const llegar = page?.llegar ?? page?.data?.llegar ?? page?.data?.data?.llegar;

	const shakespeareImage = getImageSrc(section0?.imageId);
	const panteonImage = getImageSrc(section1?.imageId);
	const luxemburgoImage = getImageSrc(section2?.imageId);
	const torreImage = getImageSrc(section3?.imageId);
	const picnicImage = getImageSrc("picnic");
	const torreStickerImage =
		getImageSrc("torre-sticker") ?? getCanvasImageUrl("torre-sticker");

	const actualHeaderColor = headerBackgroundColor || "#2C3E50";
	const contentColor = textColor || "#444";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					{headerText}
					{!isSmallScreen && logoImage && (
						<img
							src={logoImage}
							alt=""
							style={{
								width: 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			<Box sx={{ padding: "10px 12px", position: "relative", paddingTop: 0 }}>
				{introText && (
					<Box
						data-block-id="text-1"
						sx={{
							paddingTop: "12px",
							paddingBottom: "12px",
							borderRadius: "12px",
							marginBottom: 0,
							marginTop: 0,
						}}
					>
						<Box component="p" sx={estiloP}>
							{introText}
						</Box>
					</Box>
				)}

				<Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
					{/* Barrio Latino */}
					{section0 && (
						<Box data-block-id="text-2" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							{section0.sabiasQue?.[0] && (
								<Box sx={{ position: "absolute", top: -30, right: "30%" }}>
									<SabiasQue
										icono={section0.sabiasQue[0].icono}
										texto={section0.sabiasQue[0].texto}
									/>
								</Box>
							)}
							{shakespeareImage && (
								<img
									src={shakespeareImage}
									alt=""
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: "12px",
									}}
								/>
							)}
							<Box sx={{ width: "60%" }}>
								{section0.title && (
									<Box component="h3" sx={estiloH3}>
										{section0.title}
									</Box>
								)}
								{section0.body && (
									<Box component="p" sx={estiloP}>
										{section0.body}
									</Box>
								)}
							</Box>
						</Box>
					)}

					{/* El Panteón */}
					{section1 && (
						<Box data-block-id="text-3" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							{panteonImage && (
								<img
									src={panteonImage}
									alt=""
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: "12px",
									}}
								/>
							)}
							<Box sx={{ width: "60%" }}>
								{section1.title && (
									<Box component="h3" sx={estiloH3}>
										{section1.title}
									</Box>
								)}
								{section1.body && (
									<Box component="p" sx={estiloP}>
										{section1.body}
									</Box>
								)}
							</Box>
							{section1.sabiasQue?.[0] && (
								<Box
									sx={{
										position: "absolute",
										top: isSmallScreen ? 0 : -10,
										left: "33%",
									}}
								>
									<SabiasQue
										icono={section1.sabiasQue[0].icono}
										texto={section1.sabiasQue[0].texto}
									/>
								</Box>
							)}
						</Box>
					)}

					{/* Jardines de Luxemburgo */}
					{section2 && (
						<Box data-block-id="text-4" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							<Box sx={{ width: "100%" }}>
								<Box
									component="p"
									sx={{
										...estiloP,
										padding: "12px",
										backgroundColor: "#E8F4F8",
										borderRadius: "12px",
									}}
								>
									{luxemburgoImage && (
										<img
											src={luxemburgoImage}
											alt=""
											style={{
												width: isSmallScreen ? 100 : 115,
												float: "right",
												marginRight: isSmallScreen ? -20 : -34,
												marginTop: -20,
												borderRadius: 12,
											}}
										/>
									)}
									{section2.title && (
										<Box component="h3" sx={estiloH3}>
											{section2.title}
										</Box>
									)}
									{section2.body}
								</Box>
							</Box>
							{section2.sabiasQue?.[0] && (
								<Box
									sx={{
										position: "absolute",
										bottom: isSmallScreen ? -20 : 0,
										left: "60%",
									}}
								>
									<SabiasQue
										icono={section2.sabiasQue[0].icono}
										texto={section2.sabiasQue[0].texto}
									/>
								</Box>
							)}
							{torreStickerImage && (
								<img
									src={torreStickerImage}
									alt=""
									style={{
										width: 100,
										position: "absolute",
										bottom: -100,
										right: -40,
										zIndex: 99,
									}}
								/>
							)}
							{picnicImage && (
								<img
									src={picnicImage}
									alt=""
									style={{
										width: 100,
										position: "absolute",
										bottom: -100,
										left: -10,
										zIndex: 99,
									}}
								/>
							)}
							{section2.tooltipBox?.text && (
								<Box
									sx={{
										position: "absolute",
										bottom: isSmallScreen ? -80 : -60,
										left: 80,
										backgroundColor: "#fff8db",
										border: "2px solid #f5c400",
										borderRadius: "16px 16px 16px 0px",
										padding: "10px 12px",
										fontSize: 11,
										fontFamily: `${
											fontFamily || "'Poppins', sans-serif"
										} !important`,
										color: contentColor,
										boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
										maxWidth: isSmallScreen ? 200 : 300,
										zIndex: 999,
									}}
								>
									{section2.tooltipBox.text}
								</Box>
							)}
						</Box>
					)}

					{/* Torre Montparnasse */}
					{section3 && (
						<Box
							data-block-id="text-5"
							sx={{
								display: "flex",
								gap: "16px",
								position: "relative",
								marginTop: isSmallScreen ? "100px" : "80px",
							}}
						>
							{torreImage && (
								<img
									src={torreImage}
									alt=""
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: "12px",
									}}
								/>
							)}
							<Box sx={{ width: "60%" }}>
								{section3.title && (
									<Box component="h3" sx={estiloH3}>
										{section3.title}
									</Box>
								)}
								{section3.body && (
									<Box component="p" sx={estiloP}>
										{section3.body}
									</Box>
								)}
							</Box>
						</Box>
					)}

					{/* Cómo llegar */}
					{llegar?.title && (
						<Box
							data-block-id="text-6"
							sx={{
								backgroundColor: "#E8F4F8",
								border: "3px solid #2C3E50",
								borderRadius: "12px",
								padding: "8px 20px",
								fontSize: 13,
								fontFamily: `${
									fontFamily || "'Poppins', sans-serif"
								} !important`,
								color: "#333",
								marginBottom: isSmallScreen ? "12px" : 0,
							}}
						>
							<Box
								component="h4"
								sx={{
									margin: 0,
									marginBottom: "8px",
									fontSize: 13,
									fontWeight: 700,
									display: "flex",
									alignItems: "center",
									gap: "6px",
									color: "#2C3E50",
									textTransform: "uppercase",
								}}
							>
								{llegar.title}
							</Box>
							{llegar.content && (
								<Box
									component="div"
									dangerouslySetInnerHTML={{ __html: llegar.content }}
									sx={{
										paddingLeft: "20px",
										margin: 0,
										lineHeight: 1.5,
										"& ul": { paddingLeft: "20px", margin: 0 },
									}}
								/>
							)}
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template152.thumbnail = "/images/template-1.png";

export default Template152;
